import React, { useState, useEffect } from 'react';
import { X, Battery, AlertTriangle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { NOTIFICATION_TYPES } from '../contexts/NotificationContext';

/**
 * BatteryLowPopup Component
 * 
 * Shows centered modal popup notifications for battery low alerts (12V and 48V at or below 20%)
 * These popups are shown for ALL user types and persist until manually dismissed
 * Blocks access to the app until user closes the notification
 */
const BatteryLowPopup = () => {
  const { notifications, removeNotification, markAsRead } = useNotifications();
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set());

  // Debug: Log when component renders
  useEffect(() => {
    console.log('[BatteryLowPopup] Component mounted/updated');
  });

  // Filter to only show unread battery low notifications
  // For battery low notifications, we want to show ALL unread ones (they should all be persistent)
  // Also filter out dismissed notifications by ID and by metadata match
  const batteryLowNotifications = notifications.filter(notif => {
    // Must be battery low type
    if (notif.type !== NOTIFICATION_TYPES.BATTERY_LOW) {
      return false;
    }

    // Must be unread
    if (notif.isRead === true) {
      return false;
    }

    // Only filter out if duration is explicitly set to auto-dismiss (positive number)
    // Battery low notifications should be persistent (duration = 0 or undefined/null)
    // But we allow them through if duration is 0, undefined, null, or missing
    if (notif.duration !== undefined && notif.duration !== null && typeof notif.duration === 'number' && notif.duration > 0) {
      // This has auto-dismiss, skip it
      return false;
    }

    // Check if dismissed by ID
    if (dismissedNotifications.has(notif.id)) {
      return false;
    }

    // Check if dismissed by metadata (in case ID doesn't match between DB and generated)
    if (notif.metadata && notif.metadata.robotId && notif.metadata.batteryType) {
      const metadataKey = `${notif.metadata.robotId}_${notif.metadata.batteryType}`;
      if (dismissedNotifications.has(metadataKey)) {
        return false;
      }
    }

    return true;
  });

  // Debug: Log when notifications change
  useEffect(() => {
    const allBatteryLow = notifications.filter(n => n.type === NOTIFICATION_TYPES.BATTERY_LOW);
    const unreadBatteryLow = notifications.filter(n => n.type === NOTIFICATION_TYPES.BATTERY_LOW && !n.isRead);

    console.log('[BatteryLowPopup] 🔔 Notifications updated:', {
      total: notifications.length,
      batteryLowAll: allBatteryLow.length,
      batteryLowUnread: unreadBatteryLow.length,
      batteryLowFiltered: batteryLowNotifications.length,
      batteryLowNotifications: batteryLowNotifications.map(n => ({
        id: n.id,
        title: n.title,
        isRead: n.isRead,
        duration: n.duration,
        metadata: n.metadata
      })),
      allBatteryLowDetails: allBatteryLow.map(n => ({
        id: n.id,
        title: n.title,
        isRead: n.isRead,
        duration: n.duration,
        metadata: n.metadata
      }))
    });

    // Log if we have notifications but they're being filtered out
    if (unreadBatteryLow.length > 0 && batteryLowNotifications.length === 0) {
      console.warn('[BatteryLowPopup] ⚠️ Found unread battery notifications but none passed filter:', unreadBatteryLow);
    }
  }, [notifications, batteryLowNotifications]);

  if (batteryLowNotifications.length === 0) {
    return null;
  }

  // Handle dismissal - mark as read and add to dismissed set
  const handleDismiss = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);

    // Mark as dismissed by ID
    setDismissedNotifications(prev => new Set(prev).add(notificationId));

    // Also mark by metadata key to handle DB vs generated ID mismatch
    if (notification && notification.metadata) {
      const metadataKey = `${notification.metadata.robotId}_${notification.metadata.batteryType}`;
      setDismissedNotifications(prev => new Set(prev).add(metadataKey));
    }

    // Mark as read in database (works for both generated IDs and DB IDs)
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Notification will still be filtered out by dismissedNotifications set
    }

    // Remove from notifications immediately
    await removeNotification(notificationId);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Show only the first notification in a modal - user must close it before seeing others
  const currentNotification = batteryLowNotifications[0];

  if (!currentNotification) {
    return null;
  }

  const batteryType = currentNotification.metadata?.batteryType || 'Battery';
  const batteryLevel = currentNotification.metadata?.batteryLevel;
  const batteryVoltage = currentNotification.metadata?.batteryVoltage;
  const isLowSoC = currentNotification.metadata?.isLowSoC;
  const isLowVoltage = currentNotification.metadata?.isLowVoltage;
  const robotName = currentNotification.metadata?.robotName || 'Unknown Robot';

  return (
    <>
      {/* Backdrop/Overlay - Blocks access to app */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[99999]"
        onClick={(e) => {
          // Prevent closing by clicking backdrop - user must click close button
          e.stopPropagation();
        }}
      />

      {/* Centered Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[100000] p-2 sm:p-4 pointer-events-none">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-auto pointer-events-auto animate-in zoom-in duration-200 max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto">
          {/* Modal Header */}
          <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600">
                  <Battery className="h-4 w-4 sm:h-6 sm:w-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-lg font-semibold text-red-900 truncate">
                    {currentNotification.title}
                  </h3>
                  {currentNotification.priority === 'HIGH' && (
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-red-200 text-red-800 mt-0.5 sm:mt-1">
                      <AlertTriangle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      Critical Alert
                    </span>
                  )}
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => handleDismiss(currentNotification.id)}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors p-1 hover:bg-red-100 rounded"
                title="Close"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <p className="text-sm sm:text-base text-gray-800 mb-3 sm:mb-4">
              {currentNotification.message}
            </p>

            {/* State of Charge Display */}
            {(batteryLevel !== undefined && batteryLevel !== null && !isNaN(batteryLevel)) && (
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center justify-between text-xs sm:text-sm text-gray-700 mb-1.5 sm:mb-2">
                  <span className="font-medium flex items-center">
                    State of Charge
                    {isLowSoC && (
                      <span className="ml-1.5 sm:ml-2 px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs bg-red-100 text-red-700 rounded">LOW</span>
                    )}
                  </span>
                  <span className={`font-bold ${isLowSoC ? 'text-red-600' : 'text-gray-700'}`}>
                    {batteryLevel.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 sm:h-3">
                  <div
                    className={`h-2.5 sm:h-3 rounded-full transition-all duration-300 ${isLowSoC ? 'bg-red-600' : 'bg-green-500'}`}
                    style={{ width: `${Math.max(0, Math.min(100, batteryLevel))}%` }}
                  />
                </div>
              </div>
            )}

            {/* Voltage Display */}
            {(batteryVoltage !== undefined && batteryVoltage !== null && !isNaN(batteryVoltage)) && (
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center justify-between text-xs sm:text-sm text-gray-700 mb-1.5 sm:mb-2">
                  <span className="font-medium flex items-center">
                    Voltage
                    {isLowVoltage && (
                      <span className="ml-1.5 sm:ml-2 px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs bg-red-100 text-red-700 rounded">LOW</span>
                    )}
                  </span>
                  <span className={`font-bold ${isLowVoltage ? 'text-red-600' : 'text-gray-700'}`}>
                    {batteryVoltage.toFixed(1)}V
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 sm:h-3">
                  {/* Voltage bar: 0V = 0%, 12V = 100% for visual representation */}
                  <div
                    className={`h-2.5 sm:h-3 rounded-full transition-all duration-300 ${isLowVoltage ? 'bg-red-600' : 'bg-green-500'}`}
                    style={{ width: `${Math.max(0, Math.min(100, (batteryVoltage / 12) * 100))}%` }}
                  />
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">Critical threshold: &lt; 12V</p>
              </div>
            )}

            {/* Robot Name */}
            <div className="mb-2 sm:mb-3">
              <p className="text-xs sm:text-sm text-gray-600">
                <span className="font-medium">Robot:</span> {robotName}
              </p>
              <p className="text-xs sm:text-sm text-gray-600">
                <span className="font-medium">Battery:</span> {batteryType}
              </p>
            </div>

            {/* Timestamp */}
            <p className="text-[10px] sm:text-xs text-gray-500">
              {formatTimestamp(currentNotification.timestamp)}
            </p>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 rounded-b-lg border-t border-gray-200 sticky bottom-0">
            <div className="flex justify-end">
              <button
                onClick={() => handleDismiss(currentNotification.id)}
                className="px-4 sm:px-6 py-1.5 sm:py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BatteryLowPopup;

